import { useMutation, useQuery } from "@apollo/client";
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Grid,
  LoadingOverlay,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";
import { ATTACH_ROLE, DETTACH_ROLE, UPDATE_USER } from "apollo/mutuations";
import { GET_ROLES } from "apollo/queries";
import ImageUploader from "components/controls/ImageUpload";
import { useEffect, useState } from "react";

const UserEditModal = ({ setOpenedEdit, editId, data }) => {
  const [roles, setRoles] = useState([]);
  const [currentRoles, setCurrentRoles] = useState([]);
  const [initialProfileImage, setInitialProfileImage] = useState(null);
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("generalInfo");

  // Password form for the new tab
  const passwordForm = useForm({
    initialValues: {
      password: "",
    },
    validate: {
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(value))
          return "Password must contain at least one uppercase letter";
        if (!/[0-9]/.test(value))
          return "Password must contain at least one number";
        if (!/[^A-Za-z0-9]/.test(value))
          return "Password must contain at least one special character";
        return null;
      },
    },
  });

  useEffect(() => {
    const userData = data.find((user) => user.id === editId);
    console.log(userData);

    if (userData) {
      form.setValues({
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
      });
      setCurrentRoles(userData.roles);
      if (userData.profile_image) {
        setInitialProfileImage(userData.profile_image);
      }
    }
  }, [editId, data]);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      phone: "",
    },
    validate: {
      phone: (value) =>
        value.length > 13 ? "Phone number must be exactly 13 characters" : null,
    },
  });

  const { loading: rolesListLoading } = useQuery(GET_ROLES, {
    onCompleted(data) {
      const rolesArray = data.roles.map((role) => ({
        label: role.name,
        value: role.name,
      }));
      setRoles(rolesArray);
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: err?.graphQLErrors?.[0]?.message || "Failed to Update User",
      });
    },
  });

  const { height } = useViewportSize();

  const previews = files.map((file, index) => {
    const imageUrl = URL.createObjectURL(file);
    return (
      <img
        key={index}
        src={imageUrl}
        alt=""
        width="130"
        onLoad={() => URL.revokeObjectURL(imageUrl)}
      />
    );
  });

  if (initialProfileImage && files.length === 0) {
    previews.unshift(
      <img key="initial" src={initialProfileImage} alt="Profile" width="130" />,
    );
  }

  const removeButton = (val) => (
    <ActionIcon size="xs" color="blue" radius="xl" variant="transparent">
      <IconX size={10} onClick={() => dettachRoleReq(val)} />
    </ActionIcon>
  );

  const [attach] = useMutation(ATTACH_ROLE);
  const [dettach] = useMutation(DETTACH_ROLE);
  const [editUser] = useMutation(UPDATE_USER);

  const setRoleDropDownValue = (val) => {
    attach({
      variables: {
        user_id: editId,
        role: val.toUpperCase(),
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Role attached!",
        });
        setCurrentRoles(data.attachRole.roles);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to attache role",
        });
      },
    });
  };

  const dettachRoleReq = (val) => {
    dettach({
      variables: {
        user_id: editId,
        role: val.toUpperCase(),
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Role detached!",
        });
        setCurrentRoles(data.detachRole.roles);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to deattach role",
        });
      },
    });
  };

  const submitGeneralInfo = () => {
    const variables = {
      id: editId,
      name: form.getInputProps("name").value,
      email: form.getInputProps("email").value,
    };
    if (files.length > 0) {
      variables.profile_image = files[files.length - 1];
    }
    editUser({
      variables,
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "User Updated Successfully",
        });
        setOpenedEdit(false);
      },
      onError(error) {
        setOpenedEdit(true);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Update User",
        });
      },
    });
  };

  const submitPassword = () => {
    editUser({
      variables: {
        id: editId,
        password: passwordForm.getInputProps("password").value,
        name: form.getInputProps("name").value,
        email: form.getInputProps("email").value,
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Password Updated Successfully",
        });
        setOpenedEdit(false);
        passwordForm.reset();
      },
      onError(error) {
        setOpenedEdit(true);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Update Password",
        });
      },
    });
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);
  };

  const availableRoles = roles.filter(
    (role) =>
      !currentRoles.some((currentRole) => currentRole.name === role.value),
  );

  return (
    <>
      <LoadingOverlay visible={rolesListLoading} overlayBlur={2} />
      <ScrollArea style={{ height: height / 1.4 }} type="auto" offsetScrollbars>
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="generalInfo">General Info</Tabs.Tab>
            <Tabs.Tab value="manageRoles">Manage Roles</Tabs.Tab>
            <Tabs.Tab value="changePassword">Change Password</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="generalInfo" pt="xs">
            <Stack>
              <TextInput
                required
                label="Name"
                placeholder="Name"
                {...form.getInputProps("name")}
              />
              <TextInput
                label="Email"
                placeholder="Email"
                type="email"
                disabled
                {...form.getInputProps("email")}
              />
              <TextInput
                required
                label="Phone"
                placeholder="Phone"
                {...form.getInputProps("phone")}
                maxLength={13}
                disabled
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9+]/g, "");
                }}
              />
              <ImageUploader
                accept="image/png, image/jpeg, image/jpg"
                defaultImage={initialProfileImage}
                multiple={false}
                onChange={(selectedFiles) => {
                  setFiles(selectedFiles);
                }}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="manageRoles" pt="xs">
            <Text size="sm">Current Roles:</Text>
            <Flex gap="md" wrap="wrap" mt="sm">
              {currentRoles.map((role, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  rightSection={removeButton(role.name)}
                >
                  {role.name}
                </Badge>
              ))}
            </Flex>
            <Select
              searchable
              data={availableRoles}
              label="Attach Role"
              placeholder="Pick a role"
              onChange={setRoleDropDownValue}
            />
          </Tabs.Panel>

          <Tabs.Panel value="changePassword" pt="xs">
            <Stack>
              <PasswordInput
                required
                label="New Password"
                placeholder="Enter new password"
                {...passwordForm.getInputProps("password")}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {activeTab === "generalInfo" && (
          <Grid>
            <Grid.Col span={12}>
              <Button
                onClick={form.onSubmit(submitGeneralInfo)}
                style={{
                  marginTop: "10px",
                  width: "30%",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                fullWidth
                type="submit"
                color="blue"
              >
                Submit
              </Button>
            </Grid.Col>
          </Grid>
        )}

        {activeTab === "changePassword" && (
          <Grid>
            <Grid.Col span={12}>
              <Button
                onClick={passwordForm.onSubmit(submitPassword)}
                style={{
                  marginTop: "10px",
                  width: "30%",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                fullWidth
                type="submit"
                color="blue"
              >
                Update Password
              </Button>
            </Grid.Col>
          </Grid>
        )}
      </ScrollArea>
    </>
  );
};

export default UserEditModal;
